import { FormControl, FormGroup, Validators } from '@angular/forms';
import { toCalendarDateString } from '../../core/utils/utils';
import { IProcessUpdatePayload } from '../../processes/models/processes.models';

/** Sentinel OCR / Person para caducidad permanente (NIF-1012). */
export const NO_EXPIRATION_DATE = '9999-01-01';

export type PersonEditFormRawValue = {
	identifier: string;
	nationality: unknown;
	expiration_date: unknown;
	expiration_permanent: boolean;
	team_number: string;
	support_number: string;
	birthdate: unknown;
	gender: unknown;
	names: string;
	surnames: string;
	address: string;
	municipality: string;
	province: string;
};

/** FormGroup con mismos controles/validadores que process-review `#OCR_FORM`. */
export function createPersonEditFormGroup(): FormGroup {
	return new FormGroup({
		identifier: new FormControl('', Validators.required),
		nationality: new FormControl('', Validators.required),
		expiration_date: new FormControl('', Validators.required),
		expiration_permanent: new FormControl(false),
		team_number: new FormControl(''),
		support_number: new FormControl(''),
		birthdate: new FormControl('', Validators.required),
		gender: new FormControl(''),
		names: new FormControl('', Validators.required),
		surnames: new FormControl('', Validators.required),
		address: new FormControl(''),
		municipality: new FormControl(''),
		province: new FormControl(''),
	});
}

/** Datepicker/`Date` o ISO con hora → `YYYY-MM-DD` (sin UTC que cambie el día). */
export function asPersonEditCalendarDate(value: unknown): string {
	if (value instanceof Date) {
		return toCalendarDateString(value);
	}
	if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
		return value.slice(0, 10);
	}
	return typeof value === 'string' ? value : '';
}

/** Solo envía sex si cumple el contrato API (M|F|Other). */
export function normalizePersonSex(value: unknown): string | undefined {
	const raw = typeof value === 'object' && value !== null && 'id' in value
		? (value as { id: unknown }).id
		: value;
	if (raw === 'M' || raw === 'F' || raw === 'Other') {
		return raw;
	}
	return undefined;
}

export function isNoExpirationDate(value: unknown): boolean {
	return asPersonEditCalendarDate(value) === NO_EXPIRATION_DATE;
}

/**
 * Aplica sentinel OCR, disable del datepicker y quita/restaura required en caducidad.
 * `state.previousExpirationDate` guarda el valor previo al marcar Permanente.
 */
export function applyExpirationPermanentState(
	form: FormGroup,
	isPermanent: boolean,
	state: { previousExpirationDate: unknown },
): void {
	const expirationControl = form.get('expiration_date');
	if (!expirationControl) {
		return;
	}
	if (isPermanent) {
		const current = expirationControl.value;
		if (!isNoExpirationDate(current)) {
			state.previousExpirationDate = current;
		}
		expirationControl.setValue(NO_EXPIRATION_DATE, { emitEvent: false });
		expirationControl.clearValidators();
		expirationControl.disable({ emitEvent: false });
	} else {
		expirationControl.enable({ emitEvent: false });
		expirationControl.setValidators(Validators.required);
		if (isNoExpirationDate(expirationControl.value)) {
			expirationControl.setValue(state.previousExpirationDate ?? '', { emitEvent: false });
		}
		state.previousExpirationDate = '';
	}
	expirationControl.updateValueAndValidity({ emitEvent: false });
}

export function transformFormToOperationsData(
	data: Record<string, unknown>,
): Array<{ key: string; value: unknown }> {
	const calendarDateKeys = new Set(['birthdate', 'expiration_date']);
	const skipKeys = new Set(['expiration_permanent']);
	const result: Array<{ key: string; value: unknown }> = [];
	for (const key of Object.keys(data)) {
		if (skipKeys.has(key)) {
			continue;
		}
		const raw = data[key];
		if (raw === null || raw === undefined) {
			continue;
		}
		let value: unknown =
			typeof raw === 'object' && raw !== null && 'id' in raw ? (raw as { id: unknown }).id : raw;
		if (calendarDateKeys.has(key)) {
			value = asPersonEditCalendarDate(value);
		}
		result.push({ key, value });
	}
	return result;
}

export interface BuildPersonUpdatePayloadInput {
	formRawValue: PersonEditFormRawValue;
	processUuid?: string;
	operationUuid?: string;
	manualValidation?: boolean;
	overwriteExisting?: boolean;
	confirmUnderageLegalAge?: boolean;
}

/** Construye el body de `PATCH /person/{uuid}` a partir del formulario de edición. */
export function buildPersonUpdatePayload(input: BuildPersonUpdatePayloadInput): IProcessUpdatePayload {
	const formData = input.formRawValue;
	const isPermanent = !!formData.expiration_permanent;
	const expirationDate = isPermanent
		? NO_EXPIRATION_DATE
		: asPersonEditCalendarDate(formData.expiration_date);
	const sex = normalizePersonSex(formData.gender);
	const payload: IProcessUpdatePayload = {
		names: formData.names,
		surnames: formData.surnames,
		identifier: formData.identifier,
		nationality:
			typeof formData.nationality === 'object' &&
			formData.nationality !== null &&
			'id' in formData.nationality
				? String((formData.nationality as { id: unknown }).id ?? '')
				: String(formData.nationality ?? ''),
		expiration_date: expirationDate,
		birth_date: asPersonEditCalendarDate(formData.birthdate),
	};
	if (input.processUuid) {
		payload.process_uuid = input.processUuid;
	}
	if (input.operationUuid) {
		payload.operation_uuid = input.operationUuid;
		payload.operations_data = transformFormToOperationsData({
			...formData,
			expiration_date: expirationDate,
		});
	}
	if (input.manualValidation) {
		payload.manual_validation = true;
	}
	if (input.overwriteExisting) {
		payload.overwrite_existing = true;
	}
	if (input.confirmUnderageLegalAge) {
		payload.confirm_underage_legal_age = true;
	}
	if (sex) {
		payload.sex = sex;
	}
	return payload;
}

type IdentityLike = {
	names?: string | null;
	surnames?: string | null;
	identifier?: string | null;
	nationality?: string | null;
	expiration_date?: string | null;
	birthdate?: string | null;
	birth_date?: string | null;
	gender?: string | null;
	sex?: string | null;
};

function normalizeCompare(value: unknown): string {
	if (value == null) {
		return '';
	}
	return String(value).trim();
}

/**
 * True si algún campo Person mapeable difiere entre identidad del proceso y OCR.
 * Usado para mostrar el botón «actualizar identidad con datos del proceso».
 */
export function ocrDiffersFromIdentity(
	identity: IdentityLike | null | undefined,
	ocr: IdentityLike | null | undefined,
): boolean {
	if (!identity || !ocr) {
		return false;
	}
	const identityBirth = identity.birthdate ?? identity.birth_date;
	const ocrBirth = ocr.birthdate ?? ocr.birth_date;
	const identitySex = identity.gender ?? identity.sex;
	const ocrSex = ocr.gender ?? ocr.sex;
	const pairs: Array<[unknown, unknown]> = [
		[identity.names, ocr.names],
		[identity.surnames, ocr.surnames],
		[identity.identifier, ocr.identifier],
		[identity.nationality, ocr.nationality],
		[asPersonEditCalendarDate(identity.expiration_date), asPersonEditCalendarDate(ocr.expiration_date)],
		[asPersonEditCalendarDate(identityBirth), asPersonEditCalendarDate(ocrBirth)],
		[normalizePersonSex(identitySex) ?? normalizeCompare(identitySex), normalizePersonSex(ocrSex) ?? normalizeCompare(ocrSex)],
	];
	return pairs.some(([a, b]) => normalizeCompare(a) !== normalizeCompare(b));
}

/** Payload PATCH desde OCR del proceso (sync identity ← process). */
export function buildPersonUpdatePayloadFromOcr(
	processUuid: string,
	ocr: IdentityLike & {
		uuid: string;
		team_number?: string | null;
		support_number?: string | null;
		address?: string | null;
		municipality?: string | null;
		province?: string | null;
	},
	options: { overwriteExisting?: boolean; confirmUnderageLegalAge?: boolean } = {},
): IProcessUpdatePayload {
	const expirationDate = asPersonEditCalendarDate(ocr.expiration_date);
	const isPermanent = isNoExpirationDate(expirationDate);
	return buildPersonUpdatePayload({
		formRawValue: {
			identifier: ocr.identifier ?? '',
			nationality: ocr.nationality ?? '',
			expiration_date: expirationDate,
			expiration_permanent: isPermanent,
			team_number: ocr.team_number ?? '',
			support_number: ocr.support_number ?? '',
			birthdate: ocr.birthdate ?? ocr.birth_date ?? '',
			gender: ocr.gender ?? ocr.sex ?? '',
			names: ocr.names ?? '',
			surnames: ocr.surnames ?? '',
			address: ocr.address ?? '',
			municipality: ocr.municipality ?? '',
			province: ocr.province ?? '',
		},
		processUuid,
		operationUuid: ocr.uuid,
		overwriteExisting: options.overwriteExisting,
		confirmUnderageLegalAge: options.confirmUnderageLegalAge,
	});
}
